// @flow
import React from 'react';
import injectSheet from 'react-jss';
import Slider from 'react-slick';
import cx from 'classnames';
import TheatreCarousel from 'shared/components/TheatreCarousel';
import SingleImage from './SingleImage';
import type { Image } from './SingleImage';
import CTABlock from './CTABlock';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

declare var IS_NODE: boolean;

if (!IS_NODE) {
  import(/* webpackChunkName: "slick-carousel" */ 'slick-carousel/slick/slick.css');
  import(/* webpackChunkName: "slick-carousel" */ 'slick-carousel/slick/slick-theme.css');
}

type CustomImageMargin = {
  margin_top: string,
  margin_right: string,
  margin_bottom: string,
  margin_left: string
}

type Props = {
  classes: Object,
  images: Array<Image>,
  padding_type: 'Non-FutureSight Padding' | 'FutureSight Padding',
  custom_image_margin: CustomImageMargin,
  custom_image_padding: string,
  image_display_type: 'FIXED' | 'ADAPTIVE',
  adaptiveImageBackgroundSize: 'cover' | 'contain',
  image_flexible_position: string,
  responsive_image?: boolean
}

type State = {
  theatreGallery: {
    open: boolean,
    startAt: number
  },
  currentSlide: number,
  visible: boolean
}

class FutureSightImage extends React.Component<Props, State> {
  static defaultProps = {
    responsive_image: false
  }

  props: Props = this.props;

  state: State = {
    theatreGallery: {
      open: false,
      startAt: -1
    },
    currentSlide: 0,
    visible: false
  };

  node = React.createRef();

  slider = React.createRef();

  handleControl = (direction) => {
    const next = this.state.currentSlide + 1 >= this.props.images.length ? 0 : this.state.currentSlide + 1;
    const prev = this.state.currentSlide - 1 <= 0 ? this.props.images.length - 1 : this.state.currentSlide - 1;

    if (direction === 1) {
      this.setState({ currentSlide: next });
      this.slider.current.slickNext();
    }
    if (direction === -1) {
      this.setState({ currentSlide: prev });
      this.slider.current.slickPrev();
    }
  }

  componentDidMount() {
    this.imageObserver();
  }

  imageObserver = () => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0
    };

    if (typeof (IntersectionObserver) !== 'undefined') {
      const observer = new IntersectionObserver(this.callback, options);

      observer.observe(this.node.current);
    } else {
      this.setVisible();
    }

  }

  callback = (entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.setVisible();
        observer.disconnect();
      }
    });

  }

  setVisible = () => {
    if (!this.state.visible) {
      this.setState( { visible: true });
    }
  }

  render() {
    const {
      classes: c,
      images,
      image_display_type: imageDisplayType = 'FIXED',
      image_flexible_position: imageFlexiblePosition = 'CENTER',
      adaptiveImageBackgroundSize = 'cover',
      responsive_image: responsiveImage
    } = this.props;
    const { theatreGallery } = this.state;

    const singleImage =
      images.length < 2 &&
      images.map((image, i) => (
        <SingleImage
          key={i}
          image={image}
          imageDisplayType={imageDisplayType}
          imageFlexiblePosition={imageFlexiblePosition}
          adaptiveImageBackgroundSize={adaptiveImageBackgroundSize}
          responsiveImage={responsiveImage}
          visible={this.state.visible}
          buttons={image.bottom_buttons}
          colorTheme={image.color_theme}
          buttonsPadding={image.bottom_buttons_padding}
          buttonMinWidth={image.bottom_buttons_min_width}
        />
      ));
    const settings = {
      arrows: false,
      dots: true,
      dotsClass: 'slick-dots',
      infinite: true,
      speed: 800,
      easing: 'ease-in-out',
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide: 0,
      autoplay: true,
      autoplaySpeed: 3000,
      draggable: false
    };

    const imageGallery = images.length >= 2 && (
      <div className={c.galleryContainer}>
        <Slider ref={this.slider} {...settings}>
          {images.map((image, i) => (
            <div key={image.url}>
              <img
                role="presentation"
                src={image.url}
                alt={image?.image_alt || ''}
                className={c.image}
                onClick={() => this.setState({
                  theatreGallery: {
                    open: true,
                    startAt: i
                  }
                })}
              />
              {image.caption && <div className={c.caption}>{image.caption}</div> }
              <CTABlock image={image} />
            </div>
          ))}
        </Slider>
      </div>
    );
    const controls = images.length >= 2 && (
      <div className={c.controlsContainer}>
        <i
          className={cx(c.controlRight, c.control, 'material-icons')}
          onClick={() => this.handleControl(1)}
        >
          keyboard_arrow_right
        </i>
        <i
          className={cx(c.controlLeft, c.control, 'material-icons')}
          onClick={() => this.handleControl(-1)}
        >
          keyboard_arrow_left
        </i>
      </div>
    );
    const theatreCarousel = theatreGallery.open &&
      <TheatreCarousel
        images={images}
        startAt={theatreGallery.startAt}
        onClose={() => this.setState({ theatreGallery: { open: false, startAt: -1 }})}
      />;

    return (
      <div className={c.container} ref={this.node}>
        {singleImage}
        {imageGallery}
        {theatreCarousel}
        {controls}
      </div>
    );
  }
}

const styles = {
  container: ({ custom_image_margin, custom_image_padding, padding_type = 'FutureSight Padding' }) => ({
    marginTop: custom_image_margin?.margin_top || '0px',
    marginRight: custom_image_margin?.margin_right || '0px',
    marginBottom: custom_image_margin?.margin_bottom || '0px',
    marginLeft: custom_image_margin?.margin_left || '0px',
    padding: padding_type === 'FutureSight Padding' ? '36px' : (custom_image_padding || '0px'),
    paddingTop: padding_type === 'FutureSight Padding' ? '26px' : null,
    paddingBottom: padding_type === 'FutureSight Padding' ? '26px' : null,
    position: 'relative',
    fontFamily: FONT_FAMILY_ROBOTO,
    /* Fixing height in the editor */
    '.js-controls &': {
      height: '100%'
    },
    '@media(max-width: 960px)': {
      maxWidth: '100%'
    }
  }),
  image: {
    width: '100%',
    height: 'auto',
    transition: 'transform .4s ease'
  },
  caption: {
    fontFamily: FONT_FAMILY_ROBOTO,
    fontSize: '17px',
    fontWeight: '600',
    lineHeight: '1.2'
  },
  galleryContainer: {
    width: '100%',
    '& img': {
      cursor: 'pointer'
    },
    '& .slick-slide': {
      height: 'auto',
      // Fix 4px gap from 'inline-block' style added by react-slick
      // https://github.com/akiran/react-slick/issues/1180
      '&> div': {
        marginBottom: '-4px'
      }
    },
    '& .slick-dots': {
      bottom: '10px'
    },
    '& .slick-dots li': {
      width: '10px',
      height: '10px'
    },
    '& .slick-dots li button:before': {
      fontSize: '10px',
      transition: 'all .2s ease',
      opacity: '0.65',
      color: 'white'
    },
    '& .slick-dots li.slick-active button:before': {
      fontSize: '12px',
      opacity: '0.95',
      color: 'white'
    }
  },
  controlsContainer: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    left: '0',
    right: '0',
    top: '0',
    bottom: '0',
    pointerEvents: 'none'
  },
  control: {
    height: '100%',
    width: '40px',
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    color: 'white',
    pointerEvents: 'auto',
    '&> .material-icons': {
      fontSize: '30px'
    }
  },
  controlLeft: {
    left: '40px'
  },
  controlRight: {
    right: '40px'
  },
};

export default injectSheet(styles)(FutureSightImage);
